import type { RootState } from '~/app/store/reducers'
import React, { useState, useEffect, useEffectEvent } from 'react'
import { useSelector } from 'react-redux'
import { Loader } from '~/shared/ui'
import { usePubNubUtils } from '~/features/member-profile/communication'
import PersonDetailLayout from './PersonDetailLayout'
import { usePatientDetail } from './usePatientDetail'
import { useNavigate, useParams } from 'react-router'

import { queueNotification } from '~/shared/notifications/actions'
import { patientsSlice } from '~/entities/person'
import { useAppDispatch } from '~/app/store/hooks'
import { isPatientUser } from '~/shared/types'

const PatientDetail: React.FC = () => {
  const pubnubClientPresent = !!useSelector((state: RootState) => state.chat.pubnub)
  const [loaded, setLoaded] = useState(false)
  const { id: patientId } = useParams()
  const navigate = useNavigate()
  const patient = useSelector((state: RootState) => state.patients.byId[Number(patientId)])
  const providers = useSelector((state: RootState) => state.providers)
  const patientDetail = usePatientDetail()
  const pubNubUtils = usePubNubUtils()
  const dispatch = useAppDispatch()

  // A different patient in the route shows the loader until its record arrives.
  const [loadingFor, setLoadingFor] = useState(patientId)
  if (patientId !== loadingFor) {
    setLoadingFor(patientId)
    setLoaded(false)
  }

  // Effect events: the effects below run on the route id (and the PubNub client arriving) and
  // read the latest `pubNubUtils` and `patientDetail` helpers, which are rebuilt every render.
  const initPresenceHeartbeat = useEffectEvent(() => {
    // Broadcast that we're viewing this patient
    const presenceHeartbeat = () =>
      pubNubUtils.updatePresence({
        viewing: true,
        patientId: Number(patientId),
      })

    presenceHeartbeat()
    return setInterval(presenceHeartbeat, 1000 * 10)
  })

  const endPresence = useEffectEvent(() => {
    pubNubUtils.updatePresence({ viewing: false })
  })

  const getPatient = useEffectEvent(async (id: string) => {
    try {
      patientDetail.switchPatient(id)
      await dispatch(patientsSlice.thunks.getPatient(id)).then(() => setLoaded(true))
    } catch (e) {
      void navigate('/members')
      dispatch(
        queueNotification({
          variant: 'error',
          message: 'Patient does not exist',
        }),
      )
    }
  })

  useEffect(() => {
    if (patientId) {
      void getPatient(patientId)
    }
  }, [patientId])

  useEffect(() => {
    // Handle presence events
    if (patientId) {
      const interval = initPresenceHeartbeat()

      return () => {
        endPresence()
        clearInterval(interval)
      }
    }
  }, [patientId, pubnubClientPresent])

  if (!isPatientUser(patient)) return null
  if (!loaded) return <Loader />

  return <PersonDetailLayout patient={patient} providers={providers} />
}

export default PatientDetail
